// Libraries
import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  addHours,
  addSeconds,
  differenceInSeconds,
  format,
  getUnixTime,
  parseISO,
  setHours,
  startOfDay,
  startOfSecond
} from 'date-fns';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Tools
import { Tools } from 'src/app/classes/shared/tools';

// Interfaces
import { Project, PutProject } from 'src/app/interfaces/api/project';
import { Track } from 'src/app/interfaces/api/track';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { Unit } from 'src/app/interfaces/api/unit';

// Services
import { ModelService } from 'src/app/services/api/models/model.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { PlayerService } from 'src/app/services/core/player/player.service';
import { WebSocketService } from 'src/app/services/core/web-socket.service';

// UI
import { ButtonComponent, DatePickerComponent, RangePickerComponent, SliderComponent } from 'ui-library';

// Dialogs
import { Model } from 'src/app/interfaces/api/model';
import { CreateEventDialogComponent } from '../dialogs/create-event-dialog/create-event-dialog.component';
import { ShareReportDialogComponent } from '../dialogs/share-report/share-report-dialog.component';

@Component({
  selector: 'app-player',
  templateUrl: './player.component.html',
  styleUrls: ['./player.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    FormsModule,
    MatSliderModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatMenuModule,
    MatOptionModule,
    MatSlideToggleModule,
    TranslateModule,
    DatePickerComponent,
    RangePickerComponent,
    SliderComponent
  ]
})

export class PlayerComponent implements OnInit, OnDestroy {
  // Services
  private readonly auth: AuthService = inject(AuthService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly modelService: ModelService = inject(ModelService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly tools: Tools = inject(Tools);
  private readonly playerService: PlayerService = inject(PlayerService);
  private readonly trackService: TrackService = inject(TrackService);
  private readonly socket: WebSocketService = inject(WebSocketService);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  // Component API
  @Output() onPlayerChanged = new EventEmitter<any>();
  @Output() onTrackerPositionsChanged = new EventEmitter<Track[]>();
  @Input() units: Unit[] = [];
  @Input() units$: BehaviorSubject<Unit[]> = new BehaviorSubject<Unit[]>([]);
  @Input() replayUnitNames: string[] = [];
  @Input() activity: any = null;
  @ViewChild(SliderComponent) sliderComponent!: SliderComponent;

  // Signals
  private readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;
  public readonly models: WritableSignal<Model[]> = signal<Model[]>([]);

  // Variables
  bufferValue: number = 0;
  playerValueSignal: Signal<number> = signal<number>(0);
  playerValue = 0;
  name = "viewer";
  isInit = false;
  playerTimeline: any;
  playMode = 'realtime';
  playerInterval: any;
  thumbLabel = false;
  playerRawValue = 0;
  playerTimeSeek = 1;
  playAllMode = false;
  liveState = true;
  liveInterval: any;
  eventTitle = "Untitled";
  eventComment = "";
  trackers: any[] = [];
  playerState: string = 'stop';
  ticks: any[] = [];
  playerDate = new Date();
  startDate = startOfDay(new Date());
  endDate = new Date();
  min = 0;
  duration: number = differenceInSeconds(this.endDate, this.startDate);
  currentDate: string = format(this.playerDate, 'dd/MM/yyyy HH:mm:ss');
  speed: number = 1;
  defaultLang: string = '';
  public viewer: any;
  project: any = {};
  showEndDate = false;
  showTimeline = true;
  startDateChanged = true;
  timezone = "Europe/Paris";
  schedule: any;
  currentDateSignal = signal<string>(this.currentDate);
  timelineWidth: number = 0;
  isDark = false;
  sliderStyles!: HTMLStyleElement;
  destroy$ = new Subject<void>();

  // Socket and streaming related properties
  socketSub: any;
  socketUnitSub: any;
  socketAttachmentSub: any;
  fastReplayTimeouts: any[] = [];
  hasStartedSendingTracks: boolean = false;
  private lastEmittedState: string = '';

  constructor() {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['startDate']) {
        this.startDate = parseISO(params['startDate']);
        this.updateCurrentDate(params['startDate']);
        this.playerDate = parseISO(params['startDate']);
      }

      if (params['endDate']) this.endDate = parseISO(params['endDate']);
      if (params['mode']) this.playMode = params['mode'];
      if (params['play'] == "true") this.onPlay();

      this.onPlayerChanged.emit(this.getEventValue());
      this.updatePlayerTimeline();
    });

    this.trackers = [];

    this.playerService.getBufferValue().pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.bufferValue = value;
    });

    effect(() => {
      const project = this.currentProject();

      if (project?.id) {
        this.project = project;
        this.timezone = project.timezone || "Europe/Paris";

        if (this.timezone.includes('+')) {
          this.timezone = "Europe/Paris";
        }

        this.schedule = this.tools.normalizeSchedule(project.schedule);
        this.getModels();
      }
    });
  }

  /////////////////////////////
  // Angular lifecycle hooks //
  /////////////////////////////

  ngOnInit() {
    this.units$.subscribe(units => {
      this.units = units
    });

    const queryParamMap = this.route.snapshot.queryParamMap;
    let startDate = queryParamMap.get("startDate");
    let endDate = queryParamMap.get("endDate");

    if (startDate && endDate)
      this.showEndDate = true;

    let currentUser = this.auth.currentUser();

    if (currentUser) {
      if (currentUser.language) {
        this.defaultLang = currentUser.language;
        this.translate.setDefaultLang(this.defaultLang);
      } else {
        this.translate.setDefaultLang(this.defaultLang);
      }

      let dates = this.tools.normalizeDates(startDate ?? this.startDate.toISOString(), endDate, this.startDateChanged, this.schedule);
      this.startDate = startDate ? parseISO(startDate) : parseISO(dates.startDate);
      this.endDate = endDate ? parseISO(endDate) : (this.isLive() ? new Date() : setHours(startOfDay(this.startDate), 20));
      this.updateCurrentDate(this.isLive() ? new Date() : this.startDate);
      let queryParamMap = this.route.snapshot.queryParamMap;
      this.playMode = queryParamMap.get("mode") ?? this.playMode;
      this.playerState = queryParamMap.get("play") ? "play" : "pause";
      this.liveState = queryParamMap.get("liveState") ?? this.playMode == "replay" ? false : true;

      if (this.isLive())
        this.goLive();

      this.setPlayerDuration();
      this.onPlayerChanged.emit(this.getEventValue())
      this.updatePlayerTimeline();

      this.initializePlayerService();
    }

    this.translate.get('connected').pipe(takeUntil(this.destroy$)).subscribe(res => { })
  }

  ngOnDestroy() {
    clearInterval(this.playerInterval);
    clearInterval(this.liveInterval);

    // Clean up socket subscriptions
    if (this.socketSub) {
      this.socketSub.unsubscribe();
    }
    if (this.socketUnitSub) {
      this.socketUnitSub.unsubscribe();
    }
    if (this.socketAttachmentSub) {
      this.socketAttachmentSub.unsubscribe();
    }

    // Clear timeouts
    this.clearTimeouts();

    // Cancel streaming service requests
    this.playerService.cancelAllRequests();

    this.destroy$.next();
    this.destroy$.complete();
  }

  updateCurrentDate(date: Date | string | null = null) {
    const targetDate = date ? (typeof date === 'string' ? parseISO(date) : date) : new Date();
    this.currentDate = format(targetDate, "dd/MM/yyyy HH:mm:ss");
    this.currentDateSignal.set(this.currentDate);
  }

  private initializePlayerService() {
    if (this.currentProject()?.database) {
      this.playerService.initDates(this.startDate.toISOString(), this.endDate.toISOString());

      if (this.liveState) {
        this.handleLiveModeTrackerUpdates();
      }
    }
  }

  private emitPlayerChangedIfNeeded(forceEmit: boolean = false): void {
    const eventValue = this.getEventValue();

    const currentStateHash = JSON.stringify({
      playMode: eventValue.playMode,
      playerState: eventValue.playerState,
      showTimeline: eventValue.showTimeline,
      liveState: eventValue.liveState,
    });

    if (forceEmit || currentStateHash !== this.lastEmittedState) {
      this.lastEmittedState = currentStateHash;
      this.onPlayerChanged.emit(eventValue);
    }
  }

  getModels(): void {
    this.modelService.getModels().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.models.set(res || []);
      },
      error: (err) => {
        console.error('Failed to load models', err);
        this.models.set([]);
      }
    });
  }

  getEventValue() {
    return {
      speed: this.speed,
      playMode: this.playMode,
      playAllMode: this.playAllMode,
      playerState: this.playerState,
      startDate: this.startDate,
      endDate: this.endDate,
      playerDate: this.playerDate.toISOString(),
      playerValue: this.playerValue,
      showTimeline: this.showTimeline,
      liveState: this.liveState
    }
  }

  onPlay() {
    if (this.playerState == 'stop') {
      this.playerState = 'play';
      this.play();
    } else if (this.playerState == 'play' || this.playerState == 'resume') {
      this.playerState = 'pause';
      this.playAllMode = false;
      clearInterval(this.playerInterval);
      clearInterval(this.liveInterval);
    } else if (this.playerState == 'pause') {
      this.playerState = 'play';
      this.play();
    }

    this.playerDate = addSeconds(this.startDate, this.playerValue);
    this.emitPlayerChangedIfNeeded(true);

    if (this.playerState == "play") {
      this.updatePlayerState(this.getEventValue());
    } else {
      this.handlePlayerPause(this.getEventValue());
    }
  }

  stop() {
    clearInterval(this.playerInterval);
    clearInterval(this.liveInterval);

    this.playerState = 'stop';
    this.playAllMode = false;
    this.playerValue = 0;
    this.playerDate = this.startDate;

    this.emitPlayerChangedIfNeeded(true);
    this.handlePlayerPause(this.getEventValue());
    this.updateCurrentDate(this.startDate);
  }

  playAll() {
    this.playAllMode = this.playAllMode ? false : true;
  }

  goLive() {
    this.liveState = true;
    clearInterval(this.liveInterval);
    clearInterval(this.playerInterval);
    this.playerState = 'play';

    let result = this.tools.normalizeDates(startOfDay(new Date()).toISOString(), new Date().toISOString(), true, this.schedule);
    this.startDate = parseISO(result.startDate);
    this.endDate = new Date();

    this.playerValue = this.setPlayerDuration();
    this.updateCurrentDate(this.endDate);
    this.updatePlayerTimeline();
    this.emitPlayerChangedIfNeeded(true);
    this.bufferValue = 0;

    this.liveInterval = setInterval(() => {
      this.endDate = new Date();
      this.updateCurrentDate(this.endDate);
      this.playerValue++;
      this.setPlayerDuration();
      this.updatePlayerTimeline();
      this.handleLiveModeTrackerUpdates();
    }, 1000);
  }

  stopLive() {
    clearInterval(this.liveInterval);
    this.liveState = false;
  }

  setPlayMode(mode: string) {
    if (mode == "replay") {
      this.playMode = 'replay';
      this.liveState = false;
      clearInterval(this.liveInterval);
    } else {

      this.playMode = 'realtime';

      clearInterval(this.playerInterval);
      this.playerState = 'stop';

    }
    this.playerValue = 0;

  }

  /**
   * Set player duration
   *
   */
  setPlayerDuration() {
    var duration = differenceInSeconds(this.endDate, this.startDate);
    this.duration = duration;
    return this.duration;
  };

  onSliderMove(event: any) {
    this.playerValue = typeof event == "number" ? event : parseInt(event.target.value, 10);
    const isToday = this.endDate.getFullYear() === new Date().getFullYear()
      && this.endDate.getMonth() === new Date().getMonth()
      && this.endDate.getDate() === new Date().getDate();

    if (this.playerValue >= this.duration && this.isReplay() && isToday) {
      if (this.sliderComponent) {
        this.sliderComponent.forceStopDrag();
      }

      const activeElement = document.activeElement as HTMLElement;
      if (activeElement) {
        activeElement.blur();
      }

      this.onPlayModeChanged('realtime');
      return;
    }

    this.onPlayerTimelineChange(this.playerValue);
    this.onPlayerTimelineClick(this.playerValue);
  }

  /**
   * Set timeline value on timeline holder moves
   *
   */
  onPlayerTimelineChange(playerValue: number | null = null) {
    if (this.isLive() || this.isRealtime()) {
      this.stopLive();
      this.playerState = "stop";
      this.onPlay();
    }

    var startDateInSeconds = getUnixTime(this.startDate);
    var playedTime = startDateInSeconds + this.playerValue;
    var endDateInSeconds = getUnixTime(this.endDate);

    if (playerValue) {
      this.playerValue = playerValue;
    }

    this.playerDate = addSeconds(this.startDate, this.playerValue);
    this.updateCurrentDate(this.playerDate);

    if (this.isRealtime() && playedTime == endDateInSeconds && !this.isLive())
      this.goLive();
    else if (this.isLive())
      this.stopLive();
    else if (this.playMode == "replay" && playedTime >= endDateInSeconds && this.playerState == "play") {
      this.onPlay();
    }

    if (playerValue !== null) {
      this.emitPlayerChangedIfNeeded(true);
    }

    this.updatePlayerState(this.getEventValue());
    if (this.playMode === "replay" && !this.liveState) {
      this.getTrackerPositionsForReplay(this.getEventValue());
    }
  };

  onPlayerTimelineClick(playerValue: number) {
    this.playerValue = playerValue;
    var startDateInSeconds = getUnixTime(this.startDate);
    var playedTime = startDateInSeconds + this.playerValue;
    var endDateInSeconds = getUnixTime(this.endDate);

    if (this.isRealtime() && playedTime == endDateInSeconds && !this.isLive())
      this.goLive();
    else if (this.isLive())
      this.stopLive();

    const eventValue = this.getEventValue();
    this.handlePlayerClick(eventValue);
  }

  getPosition() {
    if (this.playMode === "realtime") {
      clearInterval(this.playerInterval);
    }

    if (this.playAllMode && this.playerValue <= this.setPlayerDuration()) {
      clearInterval(this.playerInterval);
    }
  }

  play() {
    clearInterval(this.playerInterval);
    clearInterval(this.liveInterval);

    const checkAndLimitPlayerValue = (): boolean => {
      if (this.playerValue + this.speed > this.duration) {
        this.playerValue = this.duration;
        this.playerDate = this.endDate;
        this.updateCurrentDate(this.endDate);
        this.onPlay(); // Pause automatique
        return true; // Limite atteinte
      }
      return false; // Continuer
    };

    const incrementPlayerValue = () => {
      this.playerValue += this.speed;
      this.playerDate = addSeconds(this.startDate, this.playerValue);
      this.updateCurrentDate(this.playerDate);
    };

    // MODE REALTIME
    if (this.isRealtime()) {
      this.emitPlayerChangedIfNeeded(true);

      this.playerInterval = setInterval(() => {
        if (checkAndLimitPlayerValue()) return;

        this.endDate = new Date();
        incrementPlayerValue();

        if (this.liveState) {
          this.handleLiveModeTrackerUpdates();
        } else {
          this.getTrackerPositionsForReplay(this.getEventValue());
        }
      }, 1000);

      return;
    }

    // MODE REPLAY
    if (this.playerValue <= this.setPlayerDuration()) {
      if (!this.playAllMode) {
        // Mode normal
        this.playerInterval = setInterval(() => {
          if (checkAndLimitPlayerValue()) return;

          incrementPlayerValue();
          this.onPlayerTimelineChange();
          this.getPosition();
        }, 1000);
      } else {
        // Mode playAll
        if (this.playerValue + this.speed > this.duration) {
          this.playerValue = this.duration;
          this.playerDate = this.endDate;
        } else {
          incrementPlayerValue();
        }

        this.onPlayerTimelineChange();
      }
    } else {
      // Restart
      this.playerValue = 0;
      this.playAllMode = false;

      this.playerInterval = setInterval(() => {
        if (checkAndLimitPlayerValue()) return;

        incrementPlayerValue();
      }, 1000);
    }
  }

  pauseViewer() {
    clearInterval(this.playerInterval);
  };

  seekBackwardViewer() {
    if ((this.playerValue - (this.speed) < 1) || (this.playerValue === 0)) {
      this.playerValue = 0;
    } else {
      this.playerValue = this.playerValue - this.speed;
    }

    this.playerDate = addSeconds(this.startDate, this.playerValue);
    this.updateCurrentDate(this.playerDate);

    this.onPlayerTimelineChange();
  };

  seekForwardViewer() {
    var playerValue = this.playerValue;
    var startDateInSeconds = getUnixTime(this.startDate) * .001;
    var playedTime = startDateInSeconds + this.playerValue;
    var endDateInSeconds = getUnixTime(this.endDate) * .001;

    if ((playedTime + this.speed > endDateInSeconds) || (playedTime === endDateInSeconds)) {
      this.playerValue = this.setPlayerDuration();
      clearInterval(this.playerInterval);
    } else {
      this.playerValue = playerValue + this.speed;
    }

    this.playerDate = addSeconds(this.startDate, this.playerValue);
    this.updateCurrentDate(this.playerDate);

    // Trigger timeline change to update streaming service and get tracker positions
    this.onPlayerTimelineChange();
  };

  saveEvent() {
    const dialogRef = this.dialog.open(CreateEventDialogComponent, { data: this.getEventValue() })

    dialogRef.afterClosed().subscribe(res => {
      // if (res.event) {
      //   this.eventsService.addEvent(res.event).pipe(takeUntil(this.destroy$)).subscribe(res => {
      //     //
      //   })
      // }
    })
    // var event = {
    //   type: "User defined",
    //   title: this.eventTitle,
    //   comment: this.eventComment,
    //   startDate: parseISO(this.startDate),
    //   endDate: parseISO(this.endDate)
    // }

    // this.eventsService.updateEvent(event).pipe(takeUntil(this.destroy$)).subscribe(result => {

    //   // modal.hide();

    // })
  }

  /**
   * update player timeline
   */
  updatePlayerTimeline() {
    this.setPlayerDuration();
  }

  onModelChanged() {
    let json: PutProject = {
      id: this.project?.id,
      model: this.project?.model
    }

    this.projectService.setProject(json).pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.project.model = json.model;
      this.projectService.setCurrentProject(this.project);
      window.location.reload();
    });
  }


  onPickerChanged(result: any) {
    if (typeof result != "string" && (!result.startDate || !result.endDate)) return; // prevents having double triggering of range-picker with undefined startDate/endDate
    if (typeof result == "string") {
      this.startDateChanged = true;
    } else {
      if (typeof result.startDate != "string")
        this.startDateChanged = true;
      else if (typeof result.endDate != "string")
        this.startDateChanged = false;

    }

    this.startDate = result.startDate ? parseISO(result.startDate) : parseISO(result);
    this.endDate = result.endDate ? parseISO(result.endDate) : addHours(result, 10);

    this.setPlayMode("replay");
    this.onPlayModeChanged("replay");
    this.stop();

    const queryParams: Params = { mode: this.playMode, startDate: this.startDate.toISOString(), endDate: this.showEndDate ? this.endDate.toISOString() : undefined };

    this.router.navigate([], {
      queryParams: queryParams, queryParamsHandling: 'merge', // remove to replace all query params by provided
    });

    this.playerValue = this.setPlayerDuration();
    this.updateCurrentDate(!this.isLive() ? this.startDate : undefined);
    this.onPlayerTimelineChange();
    this.updatePlayerTimeline();

    // Handle date change internally
    const eventValue = this.getEventValue();
    this.handlePlayerClick(eventValue);
  }

  onPlayModeChanged(mode: string) {
    this.playMode = mode;
    if (mode == "realtime" || mode == "live") {
      this.updateCurrentDate();
      this.playerService.cancelAllRequests();
      this.goLive();
    }
    else {
      this.updateCurrentDate(this.startDate);
      this.onPlayerTimelineChange();
      this.updatePlayerTimeline();
    }

    // Handle mode change internally
    const eventValue = this.getEventValue();
    this.updatePlayerState(eventValue);

    const queryParams: Params = { mode: this.playMode, startDate: this.startDate.toISOString(), endDate: this.showEndDate ? this.endDate.toISOString() : undefined };
    this.router.navigate(
      [],
      {
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      });
  }

  getMoment() {
    //get project time
    let time = format(new Date(), "dd/MM/yyyy HH:mm:ss");
    return new Date(time).toISOString();
  }

  toggleTimeline(bool: boolean) {
    this.showTimeline = bool;
    this.emitPlayerChangedIfNeeded(true);
    this.updatePlayerTimeline();

    this.updatePlayerState(this.getEventValue());
  }

  isRealtime() {
    return this.playMode == 'realtime';
  }

  isReplay() {
    return this.playMode == 'replay' || this.liveState == false;
  }

  isLive() {
    return this.liveState && this.playMode != "replay";
  }

  onShowEndChange() {
    this.showEndDate = !this.showEndDate;
    if (!this.startDate || !this.endDate) return;
    const queryParams: Params = { mode: this.playMode, startDate: this.startDate.toISOString(), endDate: this.showEndDate ? this.endDate.toISOString() : undefined };
    this.router.navigate(
      [],
      {
        queryParams: queryParams,
        queryParamsHandling: 'merge', // remove to replace all query params by provided
      });

    // Handle end date change internally
    const eventValue = this.getEventValue();
    this.handlePlayerClick(eventValue);
  }

  embedLink() {
    const dialogRef = this.dialog.open(ShareReportDialogComponent, {
      width: '420px',
      data: {
        title: this.translate.instant('dashboard.share.embedMapTitle'),
        url: `${window.location.origin}/embed-viewer?project=${encodeURIComponent(this.currentProject()?.id || '')}&accessKey=${encodeURIComponent(this.currentProject()?.accessKey || '')}`,
        public: true,
        noFilter: true,
        icon: 'code'
      },
    });

    dialogRef.afterClosed().subscribe((res) => { });
  }

  // ========================================
  // PLAYER METHODS - Parent Agnostic
  // ========================================

  /**
   * Get tracker positions for replay mode
   */
  getTrackerPositionsForReplay(data: any) {

    if (!this.units || this.units.length === 0) return;

    let ids: string[] = [];

    this.units.forEach((unit) => {
      if ((unit.visible || this.replayUnitNames.length) && unit.trackers && unit.trackers.length) {
        let trackers: string[] = unit.trackers.map(t => (t as Tracker)?.id || t as string || '')
        // if(trackers.length)
        ids = ids.concat(trackers);
      }
    });


    if (ids.length === 0) return;

    const payload = {
      trackers: ids,
      createdDate: new Date(data.playerDate).toISOString(),
      speed: data.speed
    };
    this.clearTimeouts();
    this.playerService.getPositionOfTracksAtTime(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        for (let result of data) {
          let { tracks, speed, index } = result;
          if (speed) {
            const timeout = setTimeout(() => {
              this.onTrackerPositionsChanged.emit(tracks);

              if (index == (speed - 1)) {// && data.playAllMode) {
                // Emit updated player value if needed
                this.onPlayerChanged.emit(this.getEventValue());
              }
            }, (1000 / speed) * index);
            this.fastReplayTimeouts.push(timeout);
          } else {
            this.onTrackerPositionsChanged.emit(tracks);

            // if (data.playAllMode) {
            this.onPlayerChanged.emit(this.getEventValue());
            // }
          }
        }
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  /**
   * Get last position of trackers for replay mode
   */
  getLastPositionOfTrackers(data: any) {
    if (!this.units || this.units.length === 0) return;

    const ids: string[] = [];
    this.units.forEach((unit) => {
      if ((unit.visible || this.replayUnitNames.length) && unit.trackers && unit.trackers.length) {
        ids.push((unit?.trackers[0] as Tracker)?.id || unit?.trackers[0] as string || '');
      }
    });

    if (ids.length === 0) return;

    const payload = {
      startDate: this.startDate.toISOString(),
      endDate: startOfSecond(data.playerDate).toISOString(),
      trackers: ids,
      speed: data.speed || this.speed
    };

    this.playerService.getLastPositionOfTracks(payload).pipe(takeUntil(this.destroy$)).subscribe((tracks: any) => {
      if (this.playerState != "play") {
        this.onTrackerPositionsChanged.emit(tracks);
      }
    }, (err) => {
      console.error(err);
    });
  }

  /**
   * Handle live mode tracker updates
   */
  handleLiveModeTrackerUpdates() {
    if (this.playMode !== 'realtime' || !this.liveState) return;

    this.getTracks();

    this.startBufferMonitoring();
  }

  /**
   * Get tracks from WebSocket for live mode
   */
  getTracks() {
    if (!this.socketSub) {
      this.socketSub = this.socket.getUpdate("Track").pipe(takeUntil(this.destroy$)).subscribe(async (data: any) => {
        try {
          if (data.length === 0 || this.playMode !== 'realtime' || !this.liveState) {
            return;
          }

          const tracks: Track[] = [];
          for (let i = 0; i < data.length; i++) {
            if (!data[i].tracker) continue;

            const trackerId = data[i].tracker.id || data[i].tracker;
            tracks.push(data[i]);
          }

          if (tracks.length > 0) {
            this.onTrackerPositionsChanged.emit(tracks);
          }

        } catch (e) {
          console.error(e);
        }
      });
    }

    if (!this.socketUnitSub) {
      this.socketUnitSub = this.socket.getUpdate("Unit").pipe(takeUntil(this.destroy$)).subscribe(async (units: any) => {
        try {
          if (!this.liveState) return;
          if (units.length === 0) {
            return;
          }

          // Update units if needed
          units.forEach((unit: any) => {
            const oldUnitIndex = this.units.findIndex(u => u.id == unit.id);
            if (oldUnitIndex >= 0) {
              this.units[oldUnitIndex] = unit;
            } else {
              this.units.push(unit);
            }
          });

        } catch (e) {
          console.error(e);
        }
      });
    }

    if (!this.socketAttachmentSub) {
      this.socketAttachmentSub = this.socket.getUpdate("Attachment").pipe(takeUntil(this.destroy$)).subscribe(async (attachments: any) => {
        try {
          // Handle attachment updates if needed
          // this.parseAttachments(attachments);
        } catch (e) {
          console.error(e);
        }
      });
    }
  }

  /**
   * Clear all timeouts
   */
  clearTimeouts() {
    this.fastReplayTimeouts.forEach(timeout => clearTimeout(timeout));
    this.fastReplayTimeouts = [];
  }

  /**
   * Send track for a virtual unit
   */
  sendTrack(virtualUnit: any) {
    if (!virtualUnit || !virtualUnit.lastTrack) return;

    const track = virtualUnit.lastTrack;

    const query = {
      trackerName: track.tracker.name || '',
      id: track.tracker.id || '',
      tracker: track.tracker.id || '',
      lat: track.lat,
      lon: track.lon,
      z: track.z,
      database: this.currentProject()?.database || ''
    };

    this.trackService.addTrack(query).pipe(takeUntil(this.destroy$)).subscribe((newTrack: any) => {
      // Track sent successfully
    }, err => console.error(err));
  }

  /**
   * Update player state and handle mode changes
   */
  updatePlayerState(data: any) {
    this.playMode = data.playMode;
    this.startDate = new Date(data.startDate);
    this.endDate = new Date(data.endDate);
    this.showTimeline = data.showTimeline;
    this.liveState = data.playMode == "replay" ? false : data.liveState;
    this.playerState = data.playerState;

    if (this.playMode == "realtime" && data.playerState == "play") {
      this.playerService.setEndDate(this.endDate.toISOString());
    }

    if (this.playerState != "play" && this.playerService.isBufferProgressing()) {
      this.playerService.cancelAllRequests();
    }
  }

  /**
   * Handle player click events
   */
  handlePlayerClick(data: any) {
    this.playMode = data.playMode;
    this.startDate = new Date(data.startDate);
    this.endDate = new Date(data.endDate);
    this.clearTimeouts();
    this.playerService.initDates(this.startDate.toISOString(), this.endDate.toISOString());
    this.showTimeline = data.showTimeline;
    this.liveState = data.playMode == "replay" ? false : data.liveState;

    if (!this.liveState) {
      if (this.socketUnitSub) {
        this.socketUnitSub.unsubscribe();
        this.socketUnitSub = null;
      }
      if (this.socketSub) {
        this.socketSub.unsubscribe();
        this.socketSub = null;
      }
    }

    if (this.liveState) {
      this.getTracks();
    }

    if (!this.liveState) {
      this.getLastPositionOfTrackers(data);
    } else if (data.playerState != 'play') {
      this.playerService.cancelAllRequests();
    }
  }

  /**
   * Handle player pause events
   */
  handlePlayerPause(data: any) {
    // this.playerService.calculateBufferPercentage(new Date(data.playerDate).toISOString(), this.trackers.map(t => t.id));
    this.playerService.cancelAllRequests();
  }

  /**
   * Update buffer percentage from streaming service
   */
  updateBufferPercentage() {
  }

  /**
   * Start monitoring buffer progress
   */
  startBufferMonitoring() {
    if (this.liveState && this.playerState === 'play') {
      this.updateBufferPercentage();
    }
  }
}
